/**
 * 分页遍历工具
 *
 * 提供通用的全量数据获取功能，自动处理分页逻辑。
 * 从 data-management.ts 等文件中提取的重复模式统一实现。
 */

/**
 * 分页响应数据结构
 */
export interface PageResponse<T> {
  /** 当前页的数据项列表 */
  items: T[]
  /** 数据总条数 */
  total: number
  /** 当前页码（从 1 开始） */
  page: number
  /** 每页大小 */
  pageSize: number
}

/**
 * fetchAllPages 函数的配置选项
 */
interface FetchAllPagesOptions<T> {
  /**
   * 分页数据获取函数
   * @param params 分页参数（page 和 pageSize）
   * @returns 包含分页数据的 Promise
   */
  fetchFn: (params: { page: number; pageSize: number }) => Promise<{ data: PageResponse<T> }>

  /**
   * 每页数据条数，默认为 100
   */
  pageSize?: number

  /**
   * 可选的响应格式标准化函数
   * 用于将不同的后端响应格式转换为统一的 PageResponse 结构
   *
   * @param raw 原始响应数据
   * @returns 标准化后的 PageResponse
   */
  normalizeFn?: (raw: unknown) => PageResponse<T>
}

/**
 * 遍历所有分页并返回完整的数据列表
 *
 * 自动处理分页逻辑：
 * - 从第 1 页开始请求
 * - 每次获取一页数据后追加到结果列表
 * - 当已获取数据达到总数或当前页无数据时停止
 *
 * @param options 配置选项
 * @returns 包含所有分页数据的数组
 *
 * @example
 * ```typescript
 * // 基础用法：使用默认配置
 * const allSubjects = await fetchAllPages({
 *   fetchFn: (params) =>
 *     http.get<PageResponse<SubjectDto>>('/subjects', params)
 * })
 *
 * // 高级用法：自定义 pageSize 和 normalizeFn
 * const allClasses = await fetchAllPages({
 *   fetchFn: (params) => http.get('/classes', params),
 *   pageSize: 50,
 *   normalizeFn: (raw) => normalizePageResponse<ClassDto>(raw)
 * })
 * ```
 */
export async function fetchAllPages<T>(options: FetchAllPagesOptions<T>): Promise<T[]> {
  const { fetchFn, pageSize = 100, normalizeFn } = options

  let page = 1
  const items: T[] = []

  while (true) {
    const response = await fetchFn({ page, pageSize })

    // 如果提供了 normalizeFn，则对原始数据进行标准化处理
    const pageResult: PageResponse<T> = normalizeFn
      ? normalizeFn(response.data)
      : response.data

    items.push(...pageResult.items)

    // 终止条件：已获取全部数据或当前页无数据
    if (items.length >= pageResult.total || pageResult.items.length === 0) {
      return items
    }

    page += 1
  }
}
